import type { Props as ReactSelectProps } from 'react-select';
import type CreatableSelect from 'react-select/creatable';
import type {
  ControlProps,
  MenuListProps,
  OptionProps,
  MultiValueProps,
} from 'react-select';
import type {
  FieldWrapProps,
  UncontrolledFieldProps,
  ControlledFieldWrapProps,
  SelectOption,
  SelectFieldIconProps,
} from '@leafwell/components';
import type { UseSelectAsyncProps } from 'hooks/useSelectAsync';

import React, { lazy, Suspense, useMemo, useCallback, useRef } from 'react';
import { components } from 'react-select';

import {
  FieldWrap,
  Toggle,
  SelectIconWrapper,
  useFieldStyles,
} from '@leafwell/components';
import Image from 'next/image';

import { useSelectAsync } from 'hooks/useSelectAsync';

const ReactSelectCreatable = lazy(() => import('react-select/creatable'));
const ReactSelect = lazy(() => import('react-select'));

export const MAX_BADGES_PLACEHOLDER = '{count}';

export type SelectGroup = {
  label?: string;
  options: SelectOption[];
};

export type SelectList = (SelectOption | SelectGroup)[];

export type CustomReactSelect = ReactSelectProps<
  SelectOption,
  boolean,
  SelectGroup
>;

export type DefaultSelectProps = Pick<
  CustomReactSelect,
  'hideSelectedOptions'
> &
  SelectFieldIconProps & {
    async?: boolean;
    closeMenuOnSelect?: CustomReactSelect['closeMenuOnSelect'];
    creatable?: boolean;
    loading?: boolean;
    loadingMessage?: CustomReactSelect['loadingMessage'];
    maxBadges?: CustomReactSelect['maxBadges'];
    maxBadgesMessage?: CustomReactSelect['maxBadgesMessage'];
    multiple?: boolean;
    noOptionsMessage?: CustomReactSelect['noOptionsMessage'];
    options: SelectList;
  };

export type AsyncSelectProps = DefaultSelectProps & UseSelectAsyncProps;

export type ExtractSelectOptions<Async> = Async extends true
  ? AsyncSelectProps
  : DefaultSelectProps;

export type SelectFieldUncontrolledProps<Async> = UncontrolledFieldProps &
  ExtractSelectOptions<Async>;

export type SelectFieldControlledProps<Async> = ControlledFieldWrapProps &
  ExtractSelectOptions<Async> &
  Pick<FieldWrapProps, 'labelSize'>;

export type SelectComponent = CustomReactSelect;

export type ExtractSelectComponentType<Creatable> = Creatable extends true
  ? React.LazyExoticComponent<CreatableSelect>
  : CustomReactSelect;

export type SelectForwardRef = <Async extends boolean>(
  props: SelectFieldUncontrolledProps<Async> & {
    ref?: React.ForwardedRef<SelectComponent>;
  },
) => JSX.Element;

const Control = (props: ControlProps) => {
  const styles = useFieldStyles({
    disabled: props?.isDisabled,
    focused: props.isFocused,
    highlight: props?.selectProps?.highlight,
    invalid: props?.selectProps?.['aria-invalid'] as boolean,
    theme: props?.selectProps?.innerTheme,
  });

  return (
    <components.Control
      {...{
        ...props,
        className: [
          styles,
          'flex items-center justify-between gap-3',
          'text-base',
          props?.selectProps?.controlClassName || '',
        ].join(' '),
      }}
    />
  );
};

const ClearIndicator = props => (
  <components.ClearIndicator
    {...{
      ...props,
      className: 'select-clear cursor-pointer mr-3',
    }}
  >
    <Image
      src="/cross.svg"
      width={9}
      height={9}
      alt=""
      className="w-2.5 my-reset"
    />
  </components.ClearIndicator>
);

const DropdownIndicator = props => (
  <components.DropdownIndicator
    {...{
      ...props,
      className: 'select-dropdown cursor-pointer',
    }}
  >
    <Image
      src="/caret.svg"
      width={12}
      height={7}
      alt=""
      className="w-3 my-reset"
    />
  </components.DropdownIndicator>
);

const Group = props => (
  <components.Group
    {...{
      ...props,
      innerProps: {
        ...props.innerProps,
        className: 'select-group first:pt-3 pt-6',
      },
    }}
  />
);

const IndicatorSeparator = () => null;

const Input = props => (
  <components.Input
    {...{
      ...props,
      'aria-label': 'select input',
      className:
        'inline-grid grid-cols-[min-content_0] before:content-[attr(data-value)] select-input',
    }}
  />
);

const MenuList = (props: MenuListProps<SelectOption>) => {
  const styles = useFieldStyles({
    highlight: props?.selectProps?.highlight,
    theme: props?.selectProps?.innerTheme,
    type: 'select' as const,
  });

  return (
    <components.MenuList
      {...{
        ...props,
        innerProps: {
          ...props.innerProps,
          className: [styles, 'p-reset'].join(' '),
        },
      }}
    />
  );
};

const Option = ({ children, ...props }: OptionProps<SelectOption>) => {
  const classNames =
    'text-sm cursor-pointer px-3 py-2 bg-white hover:bg-green-100 hover:secondary:lilac-100 transition-colors';

  return props?.isMulti && !props.selectProps.hideSelectedOptions ? (
    <components.Option
      {...{
        ...props,
        className: [classNames, 'flex items-center'].join(' '),
      }}
    >
      <Toggle {...{ checked: props?.isSelected }} />
      {children}
    </components.Option>
  ) : (
    <components.Option
      {...{
        ...props,
        className: [
          classNames,
          props.isFocused ? 'bg-green-100 secondary:bg-lilac-100' : '',
          props.isSelected ? 'bg-green secondary:bg-lilac-100' : '',
        ].join(' '),
      }}
    >
      {children}
    </components.Option>
  );
};

const MultiValue = ({
  index,
  getValue,
  ...props
}: MultiValueProps<SelectOption>) => {
  const value = getValue();
  const maxBadges = props?.selectProps?.maxBadges;
  const message = useMemo(() => {
    const overflow = value.slice(maxBadges).length.toString();

    return props?.selectProps?.maxBadgesMessage.replace(
      MAX_BADGES_PLACEHOLDER,
      overflow,
    );
  }, [props?.selectProps?.maxBadgesMessage, value]);

  if (index === maxBadges) {
    return (
      <span
        className={[
          'order-last text-sm leading-tight',
          maxBadges > 0 ? 'ml-auto' : '',
        ].join(' ')}
      >
        {message}
      </span>
    );
  }

  if (index < maxBadges) {
    return (
      <components.MultiValue
        {...{
          ...props,
          getValue,
          index,
          innerProps: {
            ...props.innerProps,
            className:
              'grid grid-flow-col items-center bg-green secondary:bg-lilac-300 text-sm px-2 py-1 gap-2 rounded',
          },
        }}
      />
    );
  }

  return null;
};

const MultiValueLabel = props => (
  <components.MultiValueLabel
    {...{
      ...props,
      innerProps: {
        ...props.innerProps,
        className: 'cursor-default leading-tight user-select-none',
      },
    }}
  />
);

const MultiValueRemove = props => (
  <components.MultiValueRemove
    {...{
      ...props,
      innerProps: {
        ...props.innerProps,
        className: 'select-remove cursor-pointer',
      },
    }}
  >
    <Image src="/cross.svg" width={9} height={9} alt="" className="w-2" />
  </components.MultiValueRemove>
);

const Placeholder = props => (
  <components.Placeholder
    {...{
      ...props,
      innerProps: {
        ...props.innerProps,
        className:
          'absolute text-gray-600 max-w-full pr-3 whitespace-nowrap overflow-hidden overflow-ellipsis',
      },
    }}
  />
);

const SingleValue = props => (
  <components.SingleValue
    {...{
      ...props,
      innerProps: {
        ...props.innerProps,
        className:
          'absolute max-w-full pr-3 whitespace-nowrap overflow-hidden overflow-ellipsis',
      },
    }}
  />
);

const ValueContainer = props => {
  const maxBadges = props?.selectProps?.maxBadges;

  return (
    <components.ValueContainer
      {...props}
      className={[
        'grow select-value flex items-center relative overflow-hidden gap-1',
        maxBadges === 0 ? '' : 'flex-wrap',
      ].join(' ')}
    />
  );
};

export const SelectField = React.forwardRef(
  <Async extends boolean>(
    {
      async = false,
      className = '',
      creatable = false,
      disabled = false,
      id,
      hideSelectedOptions = true,
      icon,
      invalid,
      loading = false,
      maxBadges = 100,
      maxBadgesMessage = `+ ${MAX_BADGES_PLACEHOLDER} options`, // use MAX_BADGES_PLACEHOLDER variable to be replaced with actual badges count
      onChange,
      options = [],
      multiple = false,
      theme,
      value,
      ...rest
    }: SelectFieldUncontrolledProps<Async>,
    ref,
  ) => {
    const flattenOptions = useCallback(
      (options: SelectList): SelectOption[] =>
        options.reduce((list, current) => {
          const groupOptions = (current as SelectGroup)?.options;

          if (groupOptions?.length) {
            return [...list, ...groupOptions];
          }

          return [...list, current];
        }, []),
      [],
    );

    /*
     * Store available options
     */
    const availableOptions = useRef<SelectOption[]>(flattenOptions(options));

    /*
     * Async component settings
     */
    const asyncSettings = useSelectAsync({
      defaultOptions: options,
      loadOnRender: (rest as SelectFieldUncontrolledProps<true>)?.loadOnRender,
      loadOptions: async (search?: string, isInfiniteLoading?: boolean) => {
        const options = await (
          rest as SelectFieldUncontrolledProps<true>
        )?.loadOptions(search, isInfiniteLoading);

        /*
         * Store async options in ref
         */
        const newOptions = flattenOptions(options).filter(
          newOption =>
            !availableOptions.current.some(
              oldOption => oldOption.value === newOption.value,
            ),
        );

        availableOptions.current.push(...newOptions);

        return options;
      },
    });

    /*
     * Get react-select component to render
     */
    {
      /* @ts-ignore */
    }
    const Component: ExtractSelectComponentType<typeof creatable> = useMemo(
      () => (creatable ? ReactSelectCreatable : ReactSelect),
      [creatable],
    );

    /*
     * Get current selected options from value
     */
    const getSelectedOptions = useCallback(
      value => {
        if (!value) {
          return null;
        }

        if (!multiple) {
          return availableOptions.current.find(
            option => option.value === value,
          );
        }

        return availableOptions.current.filter(current => {
          return value.includes(current.value);
        }, []);
      },
      [multiple, availableOptions.current],
    );

    const onChangeSelect = useCallback(
      (currentOption: SelectOption | SelectOption[] | null) => {
        const isArray = Array.isArray(currentOption);
        const newValue = isArray
          ? currentOption.map(({ value }) => value)
          : currentOption.value;

        onChange(newValue);
      },
      [async, creatable, availableOptions, onChange],
    );

    return (
      <Suspense fallback={<div className="min-h-50"></div>}>
        <SelectIconWrapper {...{ icon }}>
          {/* @ts-ignore */}
          <Component
            {...{
              'aria-invalid': invalid,
              cacheOptions: true,
              className: 'leafwell-select',
              components: {
                ClearIndicator,
                Control,
                DropdownIndicator,
                Group,
                IndicatorSeparator,
                Input,
                MenuList,
                MultiValue,
                MultiValueLabel,
                MultiValueRemove,
                Option,
                Placeholder,
                SingleValue,
                ValueContainer,
              },
              hideSelectedOptions,
              inputId: id,
              isClearable: multiple,
              isDisabled: disabled,
              isMulti: multiple,
              menuShouldScrollIntoView: true,
              onChange: onChangeSelect,
              ref,
              styles: {
                clearIndicator: () => null,
                dropdownIndicator: () => null,
                control: () => null,
                group: () => null,
                input: () => null,
                menu: provided => ({
                  ...provided,
                  margin: `-1px 0 0 0`,
                  padding: 0,
                }),
                menuPortal: provided => ({
                  ...provided,
                  zIndex: 10,
                }),
                multiValue: () => null,
                multiValueLabel: () => null,
                multiValueRemove: () => null,
                option: () => null,
                placeholder: () => null,
                singleValue: () => null,
                valueContainer: () => null,
              },
              value: useMemo(() => getSelectedOptions(value), [value]),
              controlClassName: [icon ? 'pl-10' : '', className].join(' '),
              innerTheme: theme,
              maxBadges,
              maxBadgesMessage,
              ...rest,
              ...(async
                ? {
                    ...asyncSettings,
                    isLoading: loading || asyncSettings.isLoading,
                  }
                : { isLoading: loading, options }),
            }}
          />
        </SelectIconWrapper>
      </Suspense>
    );
  },
) as SelectForwardRef;

export const SelectFieldControlled = ({
  async,
  options,
  placeholder,
  theme,
  ...props
}: SelectFieldControlledProps<typeof async>): JSX.Element => (
  <FieldWrap {...{ theme, ...props }}>
    {/* @ts-ignore: missing props are added via React.cloneElement in FieldWrap */}
    <SelectField
      {...{
        async,
        options,
        placeholder,
        theme,
      }}
    />
  </FieldWrap>
);
